import { GoogleAuthProvider, User, onAuthStateChanged, signInWithPopup, signOut } from 'firebase/auth';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import { auth } from '../firebase';

interface AuthState {
  user: User | null;
  loading: boolean;
  /** Set when a signed-in account is not on the allowlist (it is signed out). */
  authError: string;
  signIn: () => Promise<void>;
  logOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

// Allowlist of accounts permitted to use the app. The real enforcement is server-side
// (every Cloud Function's assertAuthed + the Firestore rules); this client gate just
// gives a clean "access restricted" UX instead of a broken, empty app. Empty list ⇒
// allow any verified account (e.g. local emulator dev).
const ALLOWED_EMAILS = String(import.meta.env.VITE_ALLOWED_EMAILS ?? '')
  .split(',')
  .map((s) => s.trim().toLowerCase())
  .filter(Boolean);

function isAllowed(u: User | null): boolean {
  if (!u || !u.emailVerified) return false;
  if (ALLOWED_EMAILS.length === 0) return true;
  return ALLOWED_EMAILS.includes((u.email ?? '').toLowerCase());
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState('');

  useEffect(() => {
    return onAuthStateChanged(auth, (u) => {
      if (u && !isAllowed(u)) {
        // Not authorized — bounce them and explain (server rules already deny all data).
        void signOut(auth);
        setAuthError('Access is restricted to authorized accounts.');
        setUser(null);
        setLoading(false);
        return;
      }
      setAuthError('');
      setUser(u);
      setLoading(false);
    });
  }, []);

  const signIn = useCallback(async () => {
    setAuthError('');
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    await signInWithPopup(auth, provider);
  }, []);

  const logOut = useCallback(async () => {
    await signOut(auth);
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, loading, authError, signIn, logOut }),
    [user, loading, authError, signIn, logOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
